// ds-typo-ignore-file — mockup simulates report viewer UI, not marketing site DS
"use client"
import { blobMediaUrl } from "@/lib/blob-url"

/**
 * Static recreation of the OrganSystemNavSidebar from the health report app.
 * Pixel-exact values extracted from my.aheadhealth.com, scaled to ~87% for iPad mockup.
 */

import { cn } from "@/lib/utils"
import { m } from "framer-motion"
import Image from "next/image"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { MockupDate } from "@/lib/mockup-date"
import { createViewportFadeUp } from "@/lib/motion"

const COLORS = {
  deepGreen: "#408c49",
  lightGreen: "#b8c664",
  sand: "#c8b59d",
  terracotta: "#ed9062",
}

const anim = (delay: number) =>
  createViewportFadeUp(delay, { y: 12, scale: 0.97 })

// Literal union of the sidebar system-label keys so `t(item.nameKey)`
// picks next-intl's single-argument overload — the `report` namespace
// contains other keys that carry `{placeholder}` tokens, and a wider
// `MessageKey<"report">` would force the 2-arg overload.
type NavItemKey =
  | "mockupSystemBrain"
  | "mockupSystemCardiovascular"
  | "mockupSystemMusculoskeletal"
  | "mockupSystemLiver"
  | "mockupSystemKidneys"
  | "mockupSystemVitamins"
  | "mockupSystemEndocrinology"

type NavItem = {
  nameKey: NavItemKey
  active?: boolean
  stats?: { optimal: number; inRange: number; outOfRange: number }
  findings?: { count: number; dots: string[] }
}

const MOCK_NAV: NavItem[] = [
  {
    nameKey: "mockupSystemBrain",
    stats: { optimal: 4, inRange: 8, outOfRange: 0 },
    findings: { count: 2, dots: [COLORS.sand, COLORS.lightGreen] },
  },
  {
    nameKey: "mockupSystemCardiovascular",
    stats: { optimal: 6, inRange: 3, outOfRange: 1 },
  },
  {
    nameKey: "mockupSystemMusculoskeletal",
    active: true,
    stats: { optimal: 2, inRange: 5, outOfRange: 2 },
    findings: {
      count: 3,
      dots: [COLORS.sand, COLORS.terracotta],
    },
  },
  {
    nameKey: "mockupSystemLiver",
    stats: { optimal: 5, inRange: 7, outOfRange: 0 },
  },
  {
    nameKey: "mockupSystemKidneys",
    stats: { optimal: 3, inRange: 4, outOfRange: 0 },
  },
  {
    nameKey: "mockupSystemVitamins",
    stats: { optimal: 8, inRange: 2, outOfRange: 1 },
  },
  {
    nameKey: "mockupSystemEndocrinology",
    stats: { optimal: 4, inRange: 3, outOfRange: 0 },
  },
]

function MiniRangeBar({
  stats,
}: {
  stats: { optimal: number; inRange: number; outOfRange: number }
}) {
  const total = stats.optimal + stats.inRange + stats.outOfRange
  if (total === 0)
    return <div className="h-1 w-[56px] rounded-full bg-zinc-200" />

  return (
    <div className="flex h-1 w-[56px] items-center gap-[1px] overflow-hidden">
      {stats.optimal > 0 && (
        <div
          className="h-full rounded-full"
          style={{
            width: `${(stats.optimal / total) * 100}%`,
            minWidth: 3,
            backgroundColor: COLORS.deepGreen,
          }}
        />
      )}
      {stats.inRange > 0 && (
        <div
          className="h-full rounded-full"
          style={{
            width: `${(stats.inRange / total) * 100}%`,
            minWidth: 3,
            backgroundColor: COLORS.lightGreen,
          }}
        />
      )}
      {stats.outOfRange > 0 && (
        <div
          className="h-full rounded-full"
          style={{
            width: `${(stats.outOfRange / total) * 100}%`,
            minWidth: 3,
            backgroundColor: COLORS.terracotta,
          }}
        />
      )}
    </div>
  )
}

/* -- Report selector card ---------------------------------------- */

function ReportSelector() {
  const t = useTranslations("report")
  const locale = useLocale()
  return (
    <m.div
      {...anim(0.55)}
      data-explainer="selector"
      className="rounded-[15px] bg-white p-1.5"
      style={{ boxShadow: "0 0 30px rgba(45,45,45,0.04)" }}
    >
      {/* Selected report button */}
      <div className="rounded-[12px] bg-neutral-100 p-2.5">
        <div className="flex items-center gap-2">
          {/* Report icon — Advanced package */}
          <Image
            src={blobMediaUrl("/images/report/advanced-package.webp")}
            alt=""
            width={32}
            height={32}
            className="h-8 w-8 shrink-0 rounded-lg object-cover"
          />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] text-neutral-600">
              {t("mockupSelectedReport")}
            </div>
            <div className="text-foreground text-[12px] font-medium">
              {t("mockupReportVariant")}
            </div>
            <div className="text-[10px] text-neutral-600">
              <MockupDate locale={locale} />
            </div>
          </div>
          {/* Chevron down */}
          <svg
            width="12"
            height="12"
            viewBox="0 0 10 10"
            fill="none"
            className="text-foreground/40 shrink-0"
          >
            <path
              d="M2.5 3.75L5 6.25L7.5 3.75"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
      {/* Quick links */}
      <div className="flex gap-3 px-2 py-1.5">
        <span className="flex items-center gap-0.5 text-[10px] text-neutral-600">
          <svg
            width="10"
            height="10"
            viewBox="0 0 8 8"
            fill="none"
            className="text-neutral-600"
          >
            <circle
              cx="4"
              cy="4"
              r="3"
              stroke="currentColor"
              strokeWidth="0.8"
            />
            <path
              d="M4 2.5V4.5"
              stroke="currentColor"
              strokeWidth="0.8"
              strokeLinecap="round"
            />
            <circle cx="4" cy="5.5" r="0.4" fill="currentColor" />
          </svg>
          {t("mockupViewDetails")}
        </span>
        <span className="flex items-center gap-0.5 text-[10px] text-neutral-600">
          <svg
            width="10"
            height="10"
            viewBox="0 0 8 8"
            fill="none"
            className="text-neutral-600"
          >
            <path
              d="M4 1v4.5M2 4l2 2 2-2"
              stroke="currentColor"
              strokeWidth="0.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M1.5 6.5h5"
              stroke="currentColor"
              strokeWidth="0.8"
              strokeLinecap="round"
            />
          </svg>
          {t("mockupDownloadReport")}
        </span>
      </div>
    </m.div>
  )
}

/* -- Main sidebar component -------------------------------------- */

export function ReportMockupSidebar({ className }: { className?: string }) {
  const t = useTranslations("report")
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {/* Report selector on top */}
      <ReportSelector />

      {/* Organ systems nav */}
      <m.nav
        {...anim(0.65)}
        data-explainer="sidebarSummary"
        className="flex flex-col overflow-hidden rounded-[15px] bg-white p-1.5"
        style={{ boxShadow: "0 0 30px rgba(45,45,45,0.04)" }}
      >
        {/* Section label */}
        <div className="px-2 py-1.5">
          <span className="text-foreground text-[12px] font-medium">
            {t("mockupSidebarSummary")}
          </span>
        </div>

        <ul className="flex flex-col">
          {MOCK_NAV.map((item) => (
            <li key={item.nameKey}>
              <div
                className={cn(
                  "grid items-center gap-2 rounded-lg p-2 transition-all duration-[250ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
                  item.active
                    ? "bg-neutral-100"
                    : "hover:scale-[1.02] hover:bg-neutral-100/50"
                )}
                style={{
                  gridTemplateColumns: "1fr 28px 56px",
                }}
              >
                <span
                  className={cn(
                    "text-foreground text-[12px] leading-tight",
                    item.active && "font-medium"
                  )}
                >
                  {t(item.nameKey)}
                </span>

                <div className="flex justify-end">
                  {item.findings && item.findings.count > 0 && (
                    <div className="flex items-center gap-0.5">
                      <div className="flex items-center gap-[2px]">
                        {item.findings.dots.map((color, i) => (
                          <div
                            key={i}
                            className="h-1 w-1 rounded-full"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <span className="text-foreground text-[10px] font-medium">
                        {item.findings.count}
                      </span>
                    </div>
                  )}
                </div>

                {item.stats ? <MiniRangeBar stats={item.stats} /> : <div />}
              </div>
            </li>
          ))}
        </ul>
      </m.nav>
    </div>
  )
}
